import axios from 'axios';
import backend_url from '../config/config';
import cacheService from './cacheService';

// Create axios instance with default config
const api = axios.create({
  baseURL: backend_url,
  timeout: 60000, // 60 second timeout (increased from 30s)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to attach JWT token
api.interceptors.request.use(
  (config) => {
    // Attach token if present
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 429 rate limiting
    if (error.response?.status === 429 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Extract retry delay from headers or use default
      const retryDelay = error.response.headers['retry-after'] 
        ? parseInt(error.response.headers['retry-after']) * 1000 
        : Math.random() * 2000 + 1000; // Random delay between 1-3 seconds
      
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      
      return api(originalRequest);
    }

    // Handle authentication errors
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('token');
      // Redirect to login if not already there
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
      return Promise.reject(new Error('Authentication failed. Please login again.'));
    }

    // Handle network errors (backend not running, no internet, timeout)
    const isConnectionRefused =
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNREFUSED' ||
      error.message?.includes('Connection refused') ||
      error.message?.includes('ERR_SOCKET_NOT_CONNECTED');
    const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
    if (isConnectionRefused) {
      return Promise.reject(
        new Error(
          'Cannot reach the server. Start the backend: open a terminal, go to StoreApp/backend and run: npm start'
        )
      );
    }
    if (isTimeout || error.code === 'NETWORK_ERROR') {
      return Promise.reject(new Error('Network connection failed. Please check your internet connection and try again.'));
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      return Promise.reject(new Error('Server error. Please try again later.'));
    }

    return Promise.reject(error);
  }
);

/** After GRN create/update/post, supplier unit prices may change — bust related caches. */
function invalidateAfterGrnChange() {
  cacheService.keys().forEach((k) => {
    if (
      k.startsWith('procurement_orders') ||
      k.startsWith('procurement_order_') ||
      k.startsWith('supplier_items') ||
      k.startsWith('item_details_')
    ) {
      cacheService.delete(k);
    }
  });
  cacheService.clearItemsData();
}

// Cached API methods
const getCached = async (url, cacheKey, ttl = 5 * 60 * 1000) => {
  // Check cache first
  const cached = cacheService.get(cacheKey);
  if (cached) {
    return { data: cached, fromCache: true };
  }

  // Fetch from API
  const response = await api.get(url);
  cacheService.setWithTTL(cacheKey, response.data, ttl);
  return { data: response.data, fromCache: false };
};

// API methods - direct axios calls without queue
export const apiService = {
  get: (url, config = {}) => api({ ...config, method: 'GET', url }),
  post: (url, data, config = {}) => api({ ...config, method: 'POST', url, data }),
  put: (url, data, config = {}) => api({ ...config, method: 'PUT', url, data }),
  patch: (url, data, config = {}) => api({ ...config, method: 'PATCH', url, data }),
  delete: (url, config = {}) => api({ ...config, method: 'DELETE', url }),

  // Cached methods
  getCached: getCached,
  
  // Branch-specific methods
  branches: {
    create: (branchData) => {
      cacheService.clearBranchesData(); // Clear cache after create
      return apiService.post('/branch', branchData);
    },
    getAll: () => getCached('/branch', 'branches_data', 10 * 60 * 1000),
    getById: (id) => getCached(`/branch/${id}`, `branch_${id}`, 10 * 60 * 1000),
    update: (id, branchData) => {
      cacheService.delete(`branch_${id}`);
      cacheService.clearBranchesData();
      return apiService.put(`/branch/${id}`, branchData);
    },
    delete: (id) => {
      cacheService.delete(`branch_${id}`);
      cacheService.clearBranchesData();
      return apiService.delete(`/branch/${id}`);
    },
  },
  
  // Item-specific methods
  items: {
    create: (itemData) => {
      cacheService.clearItemsData();
      return apiService.post('/items', itemData);
    },
    getAll: () => getCached('/items', 'items_data', 5 * 60 * 1000),
    getById: (id) => getCached(`/items/${id}`, `item_${id}`, 5 * 60 * 1000),
    update: (id, itemData) => {
      cacheService.delete(`item_${id}`);
      cacheService.clearItemsData();
      return apiService.put(`/items/${id}`, itemData);
    },
    delete: (id) => {
      cacheService.delete(`item_${id}`);
      cacheService.clearItemsData();
      return apiService.delete(`/items/${id}`);
    },
  },
  
  // Wastage-specific methods
  wastage: {
    create: (wastageData) => apiService.post('/wastage', wastageData),
    getAll: () => apiService.get('/wastage'),
    getById: (id) => apiService.get(`/wastage/${id}`),
    update: (id, wastageData) => apiService.put(`/wastage/${id}`, wastageData),
    delete: (id) => apiService.delete(`/wastage/${id}`),
  },
  
  // Order-specific methods
  orders: {
    create: (orderData) => {
      cacheService.clearOrdersData();
      return apiService.post('/orders', orderData);
    },
    getAll: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/orders?${queryString}` : '/orders';
      return getCached(url, `orders_data_${queryString}`, 2 * 60 * 1000);
    },
    getForSubmission: () => getCached('/orders/submission', 'orders_submission_data', 1 * 60 * 1000),
    getAllSimple: () => getCached('/orders/simple', 'orders_simple_data', 1 * 60 * 1000),
    getById: (id) => getCached(`/orders/${id}`, `order_${id}`, 2 * 60 * 1000),
    update: (id, orderData) => {
      cacheService.delete(`order_${id}`);
      cacheService.clearOrdersData();
      return apiService.patch(`/orders/${id}`, orderData);
    },
    delete: (id) => {
      cacheService.delete(`order_${id}`);
      cacheService.clearOrdersData();
      return apiService.delete(`/orders/${id}`);
    },
    deletePendingSalesOrder: (id) => {
      cacheService.delete(`order_${id}`);
      cacheService.clearOrdersData();
      return apiService.delete(`/orders/sales/${id}`);
    },
    updateStatus: (id, status) => {
      cacheService.delete(`order_${id}`);
      cacheService.clearOrdersData();
      return apiService.patch(`/orders/${id}`, { status });
    },
    generateNumber: ({ branchName, branchCode } = {}) => {
      const params = new URLSearchParams();
      if (branchName) params.set('branchName', branchName);
      if (branchCode) params.set('branchCode', branchCode);
      const query = params.toString();
      return apiService.get(query ? `/orders/generate-number?${query}` : '/orders/generate-number');
    },
    generateSalesNumber: () => apiService.get('/orders/generate-sales-number'),
    createSales: (payload) => {
      cacheService.clearOrdersData();
      return apiService.post('/orders/sales', payload);
    },
    getSalesOrders: () => apiService.get('/orders/sales/list'),
    getSalesOrdersHistory: () => apiService.get('/orders/sales/history/list'),
    getSalesOrderCounts: () => apiService.get('/orders/sales/counts'),
    getSalesOrderSlotCounts: () => apiService.get('/orders/sales/slot-counts'),
    getSalesOrderSlotCountsHistory: () => apiService.get('/orders/sales/history/slot-counts'),
    updateSalesOrderStatus: (id, status) => apiService.patch(`/orders/sales/${id}/status`, { status }),
    markSalesOrderPaymentReceived: (id, body) => {
      cacheService.clearOrdersData();
      return apiService.patch(`/orders/sales/${id}/payment-received`, body || {});
    },
    listSalesCustomerDebts: (params) =>
      apiService.get('/orders/sales/customer-debts', { params: params || {} }),
    markSalesOrderDelivered: (id) => apiService.patch(`/orders/sales/${id}/delivered`),
    markSalesOrderReady: (id) => apiService.patch(`/orders/sales/${id}/ready`),
    getSalesUnpaidOrders: () => apiService.get('/orders/sales/unpaid/list'),
    getSalesOrdersRevenue: (params) => apiService.get('/orders/sales/revenue', { params: params || {} }),
    getSalesDashboardKpis: () => apiService.get('/orders/sales/dashboard-kpis'),
  },

  customers: {
    list: (params) => apiService.get('/customers', { params: params || {} }),
    nextNumber: () => apiService.get('/customers/next-number'),
    getById: (id) => apiService.get(`/customers/${id}`),
    create: (data) => apiService.post('/customers', data),
    update: (id, data) => apiService.put(`/customers/${id}`, data),
    setLock: (id, locked) => apiService.patch(`/customers/${id}/lock`, { locked: Boolean(locked) }),
    remove: (id) => apiService.delete(`/customers/${id}`),
  },

  users: {
    listSalesUsers: () => apiService.get('/users/sales-users'),
  },

  cashHandover: {
    getToday: (salesUserId) => apiService.get('/cash-handover/today', { params: { salesUserId } }),
    submit: ({ salesUserId, cashInHand }) => apiService.post('/cash-handover/submit', { salesUserId, cashInHand }),
    history: () => apiService.get('/cash-handover/history'),
  },

  // Sales module: shops and menu (DB)
  sales: {
    getShops: () => apiService.get('/sales/shops'),
    createShop: (data) => apiService.post('/sales/shops', data),
    updateShop: (id, data) => apiService.put(`/sales/shops/${id}`, data),
    deleteShop: (id) => apiService.delete(`/sales/shops/${id}`),
    getMenuByShop: (shopId) => apiService.get(`/sales/shops/${shopId}/menu`),
    addMenuItem: (shopId, data) => apiService.post(`/sales/shops/${shopId}/menu`, data),
    updateMenuItem: (shopId, itemId, data) => apiService.put(`/sales/shops/${shopId}/menu/${itemId}`, data),
    removeMenuItem: (shopId, itemId) => apiService.delete(`/sales/shops/${shopId}/menu/${itemId}`),
    seedGrillNRiceMenu: () => apiService.post('/sales/shops/seed-grill-n-rice'),
  },

  // Procurement Order-specific methods
  procurementOrders: {
    create: (orderData) => {
      cacheService.keys().forEach((k) => {
        if (k.startsWith('procurement_orders') || k.startsWith('procurement_order_')) {
          cacheService.delete(k);
        }
      });
      return apiService.post('/procurement-orders', orderData);
    },
    getAll: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const cacheKey = `procurement_orders_${queryString || 'all'}`;
      const cached = cacheService.get(cacheKey);
      if (cached) {
        return Promise.resolve({ data: cached });
      }
      const url = queryString ? `/procurement-orders?${queryString}` : '/procurement-orders';
      return apiService.get(url).then((response) => {
        cacheService.set(cacheKey, response.data, 2 * 60 * 1000);
        return response;
      });
    },
    getById: (id) => {
      const cacheKey = `procurement_order_${id}`;
      const cached = cacheService.get(cacheKey);
      if (cached) {
        return Promise.resolve({ data: cached });
      }
      return apiService.get(`/procurement-orders/${id}`).then((response) => {
        cacheService.set(cacheKey, response.data, 5 * 60 * 1000);
        return response;
      });
    },
    submit: (id) => {
      cacheService.keys().forEach((k) => {
        if (k.startsWith('procurement_orders') || k.startsWith('procurement_order_')) {
          cacheService.delete(k);
        }
      });
      return apiService.post(`/procurement-orders/${id}/submit`);
    },
    receive: (id) => {
      cacheService.keys().forEach((k) => {
        if (k.startsWith('procurement_orders') || k.startsWith('procurement_order_')) {
          cacheService.delete(k);
        }
      });
      return apiService.post(`/procurement-orders/${id}/receive`);
    },
    confirm: (id) => {
      cacheService.keys().forEach((k) => {
        if (k.startsWith('procurement_orders') || k.startsWith('procurement_order_')) {
          cacheService.delete(k);
        }
      });
      return apiService.post(`/procurement-orders/${id}/confirm`);
    },
    close: (id) => {
      cacheService.keys().forEach((k) => {
        if (k.startsWith('procurement_orders') || k.startsWith('procurement_order_')) {
          cacheService.delete(k);
        }
      });
      return apiService.post(`/procurement-orders/${id}/close`);
    },
    update: (id, orderData) => {
      cacheService.keys().forEach((k) => {
        if (k.startsWith('procurement_orders') || k.startsWith('procurement_order_')) {
          cacheService.delete(k);
        }
      });
      return apiService.put(`/procurement-orders/${id}`, orderData);
    },
    delete: (id) => {
      cacheService.keys().forEach((k) => {
        if (k.startsWith('procurement_orders') || k.startsWith('procurement_order_')) {
          cacheService.delete(k);
        }
      });
      return apiService.delete(`/procurement-orders/${id}`);
    },
    generatePONumber: () => apiService.get('/procurement-orders/generate-po-number'),
  },

  goodsReceivedNotes: {
    listByPurchaseOrder: (poId) =>
      apiService.get(`/goods-received-notes/by-purchase-order/${poId}`),
    create: (data) => {
      invalidateAfterGrnChange();
      return apiService.post('/goods-received-notes', data);
    },
    getById: (id) => apiService.get(`/goods-received-notes/${id}`),
    update: (id, data) => {
      invalidateAfterGrnChange();
      return apiService.put(`/goods-received-notes/${id}`, data);
    },
    post: (id, body) => {
      invalidateAfterGrnChange();
      return apiService.post(`/goods-received-notes/${id}/post`, body);
    },
  },

  // Recipe pricing / recipes
  recipes: {
    getAll: () => apiService.get('/recipes'),
    getById: (id, params = {}) =>
      apiService.get(`/recipes/${id}`, { params: params || {} }),
    create: (recipeData) => apiService.post('/recipes', recipeData),
    update: (id, recipeData) => apiService.put(`/recipes/${id}`, recipeData),
    delete: (id) => apiService.delete(`/recipes/${id}`),
  },
  
  // Section-specific methods
  sections: {
    create: (sectionData) => {
      cacheService.clearSectionsData();
      return apiService.post('/sections', sectionData);
    },
    getAll: () => getCached('/sections', 'sections_data', 15 * 60 * 1000),
    getActive: () => getCached('/sections/active', 'sections_active', 15 * 60 * 1000),
    getById: (id) => getCached(`/sections/${id}`, `section_${id}`, 15 * 60 * 1000),
    update: (id, sectionData) => {
      cacheService.delete(`section_${id}`);
      cacheService.clearSectionsData();
      return apiService.put(`/sections/${id}`, sectionData);
    },
    delete: (id) => {
      cacheService.delete(`section_${id}`);
      cacheService.clearSectionsData();
      return apiService.delete(`/sections/${id}`);
    },
  },
  
  // Purchase Category methods
  purchaseCategories: {
    create: (categoryData) => apiService.post('/purchase-categories', categoryData),
    getAll: () => apiService.get('/purchase-categories'),
    getById: (id) => apiService.get(`/purchase-categories/${id}`),
    update: (id, categoryData) => apiService.put(`/purchase-categories/${id}`, categoryData),
    delete: (id) => apiService.delete(`/purchase-categories/${id}`),
  },
  
  // Branch Category methods
  branchCategories: {
    create: (categoryData) => apiService.post('/branch-categories', categoryData),
    getAll: () => apiService.get('/branch-categories'),
    getById: (id) => apiService.get(`/branch-categories/${id}`),
    update: (id, categoryData) => apiService.put(`/branch-categories/${id}`, categoryData),
    delete: (id) => apiService.delete(`/branch-categories/${id}`),
  },

  // Item Category methods
  itemCategories: {
    create: (categoryData) => apiService.post('/item-categories', categoryData),
    getAll: () => apiService.get('/item-categories'),
    getAllSubCategories: () => apiService.get('/item-categories/all-subcategories'),
    getById: (id) => apiService.get(`/item-categories/${id}`),
    update: (id, categoryData) => apiService.put(`/item-categories/${id}`, categoryData),
    delete: (id) => apiService.delete(`/item-categories/${id}`),
  },

  // Sub Category methods
  subCategories: {
    create: (subCategoryData) => apiService.post('/sub-categories', subCategoryData),
    getAll: () => apiService.get('/sub-categories'),
    getById: (id) => apiService.get(`/sub-categories/${id}`),
    update: (id, subCategoryData) => apiService.put(`/sub-categories/${id}`, subCategoryData),
    delete: (id) => apiService.delete(`/sub-categories/${id}`),
  },

  // Unit methods
  units: {
    create: (unitData) => apiService.post('/units', unitData),
    getAll: () => apiService.get('/units'),
    getById: (id) => apiService.get(`/units/${id}`),
    update: (id, unitData) => apiService.put(`/units/${id}`, unitData),
    delete: (id) => apiService.delete(`/units/${id}`),
  },

  // Tax methods
  taxes: {
    create: (taxData) => apiService.post('/taxes', taxData),
    getAll: () => apiService.get('/taxes'),
    getById: (id) => apiService.get(`/taxes/${id}`),
    update: (id, taxData) => apiService.put(`/taxes/${id}`, taxData),
    delete: (id) => apiService.delete(`/taxes/${id}`),
  },

  // Brand methods
  brands: {
    create: (brandData) => apiService.post('/brands', brandData),
    getAll: () => apiService.get('/brands'),
    getById: (id) => apiService.get(`/brands/${id}`),
    update: (id, brandData) => apiService.put(`/brands/${id}`, brandData),
    delete: (id) => apiService.delete(`/brands/${id}`),
  },

  // Department methods
  departments: {
    create: (departmentData) => apiService.post('/departments', departmentData),
    getAll: () => apiService.get('/departments'),
    getById: (id) => apiService.get(`/departments/${id}`),
    update: (id, departmentData) => apiService.put(`/departments/${id}`, departmentData),
    delete: (id) => apiService.delete(`/departments/${id}`),
  },

  // KCT (Kitchen Consumption Tracker) methods
  kct: {
    sections: {
      create: (sectionData) => apiService.post('/kct/sections', sectionData),
      getAll: () => apiService.get('/kct/sections'),
      getActive: () => apiService.get('/kct/sections/active'),
      getById: (id) => apiService.get(`/kct/sections/${id}`),
      update: (id, sectionData) => apiService.put(`/kct/sections/${id}`, sectionData),
      delete: (id) => apiService.delete(`/kct/sections/${id}`),
    },
    chefs: {
      create: (chefData) => apiService.post('/kct/chefs', chefData),
      getAll: () => apiService.get('/kct/chefs'),
      getActive: () => apiService.get('/kct/chefs/active'),
      getById: (id) => apiService.get(`/kct/chefs/${id}`),
      update: (id, chefData) => apiService.put(`/kct/chefs/${id}`, chefData),
      delete: (id) => apiService.delete(`/kct/chefs/${id}`),
    },
    units: {
      create: (unitData) => apiService.post('/kct/units', unitData),
      getAll: () => apiService.get('/kct/units'),
      getActive: () => apiService.get('/kct/units/active'),
      getById: (id) => apiService.get(`/kct/units/${id}`),
      update: (id, unitData) => apiService.put(`/kct/units/${id}`, unitData),
      delete: (id) => apiService.delete(`/kct/units/${id}`),
    },
    itemCategories: {
      create: (categoryData) => apiService.post('/kct/item-categories', categoryData),
      getAll: () => apiService.get('/kct/item-categories'),
      getActive: () => apiService.get('/kct/item-categories/active'),
      getById: (id) => apiService.get(`/kct/item-categories/${id}`),
      update: (id, categoryData) => apiService.put(`/kct/item-categories/${id}`, categoryData),
      delete: (id) => apiService.delete(`/kct/item-categories/${id}`),
    },
    itemNames: {
      create: (itemNameData) => apiService.post('/kct/item-names', itemNameData),
      getAll: () => apiService.get('/kct/item-names'),
      getActive: () => apiService.get('/kct/item-names/active'),
      getById: (id) => apiService.get(`/kct/item-names/${id}`),
      update: (id, itemNameData) => apiService.put(`/kct/item-names/${id}`, itemNameData),
      delete: (id) => apiService.delete(`/kct/item-names/${id}`),
    },
    setupTracking: {
      create: (trackingData) => apiService.post('/kct/setup-tracking', trackingData),
      getAll: () => apiService.get('/kct/setup-tracking'),
      getActive: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return apiService.get(queryString ? `/kct/setup-tracking/active?${queryString}` : '/kct/setup-tracking/active');
      },
      getById: (id) => apiService.get(`/kct/setup-tracking/${id}`),
      update: (id, trackingData) => apiService.put(`/kct/setup-tracking/${id}`, trackingData),
      delete: (id) => apiService.delete(`/kct/setup-tracking/${id}`),
    },
    consumptionLogs: {
      create: (logData) => apiService.post('/kct/consumption-logs', logData),
      getAll: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return apiService.get(queryString ? `/kct/consumption-logs?${queryString}` : '/kct/consumption-logs');
      },
      getStats: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return apiService.get(queryString ? `/kct/consumption-logs/stats?${queryString}` : '/kct/consumption-logs/stats');
      },
      getById: (id) => apiService.get(`/kct/consumption-logs/${id}`),
      update: (id, logData) => apiService.put(`/kct/consumption-logs/${id}`, logData),
      delete: (id) => apiService.delete(`/kct/consumption-logs/${id}`),
      deleteAll: () => apiService.delete('/kct/consumption-logs'),
    },
  }
};

// Optimized batch request function for multiple endpoints
export const batchRequest = async (requests, batchSize = 5) => {
  const results = [];
  
  for (let i = 0; i < requests.length; i += batchSize) {
    const batch = requests.slice(i, i + batchSize);
    
    try {
      const batchResults = await Promise.allSettled(
        batch.map(request => api(request))
      );
      
      results.push(...batchResults);
    } catch (error) {
      // Add failed results to maintain array length
      results.push(...batch.map(() => ({ status: 'rejected', reason: error })));
    }
  }
  
  return results;
};

// Helper function to handle common data fetching patterns
export const fetchMultipleEndpoints = async (endpoints) => {
  const requests = endpoints.map(endpoint => ({
    method: 'GET',
    url: endpoint.url,
    ...endpoint.config
  }));
  
  const results = await batchRequest(requests);
  
  const data = {};
  results.forEach((result, index) => {
    const key = endpoints[index].key;
    if (result.status === 'fulfilled') {
      data[key] = result.value.data;
    } else {
      data[key] = [];
    }
  });
  
  return data;
};

export default api;